import { motion } from "framer-motion";
import { MessageSquarePlus } from "lucide-react";
import React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../../lib/utils";

/** Viewport coordinates (same as `getBoundingClientRect()` in the top window). */
export type SelectionAnchorRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type SelectionActionPillProps = {
  anchorRect: SelectionAnchorRect;
  onClick: () => void;
  className?: string;
  children?: React.ReactNode;
  /** Pixel gap above the selection top edge (default matches artifact iframe). */
  offsetAbove?: number;
};

export function SelectionActionPill({
  anchorRect,
  onClick,
  className,
  children,
  offsetAbove = 40,
}: SelectionActionPillProps) {
  const centerX = anchorRect.left + anchorRect.width / 2;
  const topY = anchorRect.top;
  const layoutKey = `${Math.round(centerX)}-${Math.round(topY)}-${Math.round(anchorRect.width)}`;

  return createPortal(
    <motion.button
      key={layoutKey}
      type="button"
      initial={{ opacity: 0, scale: 0.92, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 520, damping: 34, mass: 0.75 }}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={{
        position: "fixed",
        left: centerX,
        top: topY - offsetAbove,
        zIndex: 99999,
      }}
      className={cn(
        "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium bg-spectrum-violet-dark hover:bg-spectrum-violet active:bg-violet-700 text-white shadow-lg shadow-black/20 transition-colors whitespace-nowrap -translate-x-1/2 cursor-pointer",
        className,
      )}
    >
      {children ?? (
        <>
          <MessageSquarePlus className="h-3.5 w-3.5" />
          Reference in chat
        </>
      )}
    </motion.button>,
    document.body,
  );
}
